import {
  ChartsGrid,
  ChartsLegend,
  ChartsXAxis,
  ChartsYAxis,
  LineChartPro,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
  BarPlot,
} from "@mui/x-charts-pro";
import { Box } from "@mui/system";
import { format } from "date-fns";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectBizOverviewConversionsFilterLoading,
  selectBizOverviewConversionsNotes,
  selectBizOverviewConversionsOutput,
} from "../../../../../../features/bizDevelopmentDashboardSlice";
import { CustomTooltip } from "../../../../../AssetManagement/Overview/Trends/helpers/components/CustomTooltip";
import { CustomItemTooltipPercentage } from "../../../../../ResuableChartTooltip/CustomItemTooltipPercentage";

interface Props {
  loadingGraph: boolean;
  showNotes: boolean;
  selectedLabels: string[];
}

const selectedTimeUnit = { label: "day" };

function LegendLineIcon({ color }: { color: string }) {
  return (
    <svg width="22" height="10" style={{ display: "block" }}>
      <line x1="0" y1="5" x2="22" y2="5" stroke={color} strokeWidth="2" />
      <circle
        cx="11"
        cy="5"
        r="3"
        fill="#fff"
        stroke={color}
        strokeWidth="1.5"
      />
    </svg>
  );
}

function CustomLegend({
  series,
}: {
  series: { label: string; color: string }[];
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: "24px",
        paddingLeft: "16px",
        marginTop: "8px",
      }}
    >
      {series.map((s) => (
        <Box
          key={s.label}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            color: "#555",
          }}
        >
          <LegendLineIcon color={s.color} />
          {s.label}
        </Box>
      ))}
    </Box>
  );
}

export default function OverviewConversionsChart({
  loadingGraph,
  showNotes,
  selectedLabels,
}: Props) {
  const conversionRows = useSelector(selectBizOverviewConversionsOutput);
  const conversionNotes = useSelector(selectBizOverviewConversionsNotes);
  const isFilterLoading = useSelector(
    selectBizOverviewConversionsFilterLoading,
  );

  const filteredActivityNotesData = useMemo(() => {
    if (!Array.isArray(conversionNotes)) return [];
    if (selectedLabels.length === 0) return conversionNotes;
    return conversionNotes.filter((note: any) => {
      if (!Array.isArray(note?.labels)) return false;
      return selectedLabels.some((selectedLabel) =>
        note.labels.includes(selectedLabel),
      );
    });
  }, [conversionNotes, selectedLabels]);

  const cvrData = useMemo(() => {
    if (!Array.isArray(conversionRows)) return [];
    return conversionRows
      .map((item: any) => {
        const date = new Date(item?.time_unit);
        const rawCvr = item?.cvr;
        if (typeof rawCvr !== "number" || Number.isNaN(rawCvr)) return null;
        const cvr = parseFloat((rawCvr * 100).toFixed(2));
        if (Number.isNaN(cvr)) return null;
        return {
          x:
            selectedTimeUnit.label === "month"
              ? format(date, "MMMM yyyy")
              : format(date, "dd-MM-yyyy"),
          y: cvr,
        };
      })
      .filter(Boolean);
  }, [conversionRows]);

  const activityNotesData = useMemo(() => {
    if (!Array.isArray(filteredActivityNotesData)) return [];
    const notesGrouped = filteredActivityNotesData.reduce(
      (acc: Record<string, number>, note: any) => {
        if (!note.activity_date) return acc;
        const date = new Date(note.activity_date);
        const formattedDate =
          selectedTimeUnit.label === "month"
            ? format(date, "MMMM yyyy")
            : format(date, "dd-MM-yyyy");
        acc[formattedDate] = (acc[formattedDate] || 0) + 1;
        return acc;
      },
      {},
    );
    return Object.entries(notesGrouped).map(([date, count]) => ({
      x: date,
      notesCount: count,
    }));
  }, [filteredActivityNotesData]);

  const sortedData = useMemo(() => {
    const allDates = new Set([
      ...cvrData.map((item: any) => item.x),
      ...activityNotesData.map((item: any) => item.x),
    ]);
    return Array.from(allDates)
      .map((date) => {
        const cvrItem = cvrData.find((item: any) => item.x === date);
        const notesItem = activityNotesData.find(
          (item: any) => item.x === date,
        );
        if (!cvrItem) return null;
        return {
          x: date,
          y: cvrItem.y,
          notesCount: notesItem?.notesCount || 0,
        };
      })
      .filter(
        (
          item,
        ): item is {
          x: any;
          y: number;
          notesCount: number;
        } => item !== null,
      )
      .sort((a: any, b: any) => {
        const dateA = new Date(a.x.split("-").reverse().join("-"));
        const dateB = new Date(b.x.split("-").reverse().join("-"));
        return dateA.getTime() - dateB.getTime();
      });
  }, [activityNotesData, cvrData]);

  const series: any = [
    {
      type: "line",
      dataKey: "y",
      color: "#5C35C9", // updated: matches reference purple
      curve: "linear",
      yAxisKey: "cvr",
      label: "CVR %",
    },
    ...(showNotes
      ? [
          {
            type: "bar",
            dataKey: "notesCount",
            color: "#FF9800",
            yAxisKey: "notes",
            label: "Notes",
          },
        ]
      : []),
  ];

  // Legend items for custom renderer
  const legendItems = [
    { label: "CVR %", color: "#5C35C9" },
    ...(showNotes ? [{ label: "Notes", color: "#FF9800" }] : []),
  ];

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      {loadingGraph || isFilterLoading ? (
        <Box sx={{ height: 380, p: 3 }}>
          <Box
            sx={{
              height: "100%",
              borderRadius: 1,
              background:
                "linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%)",
              backgroundSize: "300% 100%",
              animation: "shimmer 2s infinite linear",
            }}
          />
        </Box>
      ) : (
        <>
          <LineChartPro
            dataset={sortedData}
            series={series}
            xAxis={[
              {
                scaleType: showNotes ? "band" : "point",
                dataKey: "x",
                disableLine: true,
              },
            ]}
            yAxis={[
              {
                id: "cvr",
                dataKey: "y",
                disableLine: true,
                disableTicks: true,
                position: "right" as const,
              },
              ...(showNotes
                ? [
                    {
                      id: "notes",
                      dataKey: "notesCount",
                      disableLine: true,
                      disableTicks: true,
                      position: "left" as const,
                      min: 0,
                    },
                  ]
                : []),
            ]}
            height={380}
            margin={{ left: 28, right: 72, top: 28, bottom: 32 }}
            sx={{
              [`& .MuiChartsAxis-tickLabel`]: {
                fill: "#aaa !important",
                fontSize: "10px !important",
              },
              [`& .MuiChartsAxis-tick`]: {
                stroke: "transparent !important",
              },
              [`& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel`]: {
                fill: "#aaa !important",
                fontSize: "11px !important",
              },
              [`& .MuiChartsGrid-line`]: {
                stroke: "#00001A26",
              },
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 1.8,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                fill: "#fff",
                strokeWidth: 1.8,
                "&:hover": {
                  scale: "1.0",
                  fill: "#5C35C9",
                  strokeWidth: 2,
                  opacity: "0.8",
                },
              },
              [`& .MuiChartsLegend-root`]: {
                display: "none",
              },
            }}
            leftAxis={showNotes ? "notes" : null}
            rightAxis="cvr"
          >
            <ChartsGrid horizontal />
            {showNotes ? (
              <CustomTooltip
                activityNotesDataGlobal={filteredActivityNotesData}
                selectedTimeUnit={selectedTimeUnit}
                dataset={sortedData}
                appendPercentage={true}
              />
            ) : (
              <CustomItemTooltipPercentage />
            )}
            <ChartsXAxis />
            <ChartsYAxis />
            {showNotes && <BarPlot />}
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <CustomLegend series={legendItems} />
        </>
      )}
    </Box>
  );
}
